import { useCallback, useEffect, useMemo } from 'react'
import { useModals, useReservationFormState } from '@/store'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useLocales } from '@sevenrooms/locales'
import { VStack } from '@sevenrooms/ui-kit/layout'
import type { ThemeSpacing } from '@sevenrooms/ui-kit/theme'
import {
  useReservationsRoute,
  useSingleDayAvailability,
  type AvailabilityTimeWithUpSellCost,
  useExperiencesWithAvailability,
  type ExperienceWithAvailability,
} from '../../../../hooks'
import { OtherAvailability } from '../OtherAvailability'
import { OtherPrivateEventsAvailability } from '../OtherPrivateEventsAvailability'
import { otherVenuesAvailabilityMessages } from './otherVenuesAvailabilityMessages'
import type { OtherVenueInfo } from '@/domain'

export interface OtherVenueAvailabilityProps {
  currencyCode: string
  collapsed: boolean
  venue: OtherVenueInfo
  startDate: string
  startTime: string
  haloTimeIntervalMinutes: number
  defaultSearchTime: string
  partySize: number
  duration?: number
  selectedLangCode?: string
  startOfDayTime: string
  isMobile: boolean
  onTitleClick: (venueUrlKey: string) => void
  onTimeSlotClick: (slot: AvailabilityTimeWithUpSellCost, venue: OtherVenueInfo) => void
  onNoAvailabilities: (venueId: string) => void
  noAvailabilityMessage: string
  clientId?: string
  isGroupWidget: boolean
  primaryVenueId?: string
  showPrivateEvents?: boolean
  onPrivateEventsTimeSlotClick?: (
    slot: AvailabilityTimeWithUpSellCost,
    experience: ExperienceWithAvailability,
    venue?: OtherVenueInfo
  ) => void
  onPrivateEventsMoreDetailsClick?: (experience: ExperienceWithAvailability) => void
  onShowMoreClick?: () => void
  scrollRightMargin?: ThemeSpacing
}

export function OtherVenueAvailability({
  venue,
  currencyCode,
  collapsed,
  startDate,
  startTime,
  defaultSearchTime,
  haloTimeIntervalMinutes,
  duration,
  partySize,
  selectedLangCode = 'en',
  isMobile,
  startOfDayTime,
  onTimeSlotClick,
  onTitleClick,
  onNoAvailabilities,
  clientId,
  isGroupWidget,
  primaryVenueId,
  showPrivateEvents,
  onPrivateEventsTimeSlotClick,
  onPrivateEventsMoreDetailsClick,
  onShowMoreClick,
  scrollRightMargin,
  noAvailabilityMessage,
}: OtherVenueAvailabilityProps) {
  const { showErrorModal } = useModals()
  const { venueId, urlKey } = venue
  const { formatMessage } = useLocales()
  const { channel } = useReservationsRoute()

  const {
    formState: { actual },
  } = useReservationFormState()

  const {
    isFetching: isPrimaryAvailabilityFetching,
    data,
    errorCode,
  } = useSingleDayAvailability({
    venueId,
    venue: urlKey,
    startDate,
    startTime,
    partySize,
    selectedLangCode,
    haloTimeIntervalMinutes,
    pickedDuration: duration,
    channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
    haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
    timeSlot: defaultSearchTime,
    startOfDayTime,
    skipRequests: collapsed,
    clientId,
    primaryVenueId,
    excludePdr: true,
    actualId: actual?.id,
  })

  const {
    data: experiences,
    isFetching: isExperiencesFetching,
    isError: isExperiencesFetchingError,
  } = useExperiencesWithAvailability({
    venue: venueId,
    partySize,
    startDate,
    startTime,
    haloTimeIntervalMinutes,
    startOfDayTime,
    clientId,
    selectedLangCode,
    experienceType: 'PDR',
    skipRequests: collapsed || !showPrivateEvents,
  })

  const timeSlotsDirection = useMemo(() => {
    if (!isGroupWidget) {
      return 'row_preview'
    }
    return isMobile ? 'column' : 'row'
  }, [isGroupWidget, isMobile])

  useEffect(() => {
    if (errorCode !== undefined) {
      showErrorModal()
    }
  }, [errorCode, showErrorModal, formatMessage])

  useEffect(() => {
    if (isExperiencesFetchingError) {
      // eslint-disable-next-line no-console
      console.error(`There was an error during loading experiences for ${venue.urlKey}`)
    }
  }, [isExperiencesFetchingError, venue.urlKey])

  const experiencesToDisplay = useMemo(() => experiences?.filter(experience => !!experience.reservationTimesInHalo?.length), [experiences])

  const onVenueClick = useCallback(() => onTitleClick(venue.urlKey), [onTitleClick, venue.urlKey])
  const onTimeSlotClickCallback = useCallback(
    (timeslot: AvailabilityTimeWithUpSellCost) => onTimeSlotClick(timeslot, venue),
    [onTimeSlotClick, venue]
  )

  const venueShortDescription =
    venue.languageStrings[selectedLangCode]?.resWidgetCrossSellVenueShortDescription ||
    venue.languageStrings.en?.resWidgetCrossSellVenueShortDescription

  const isPrimaryAvailabilityEmpty = !isPrimaryAvailabilityFetching && data?.reservationTimesInHalo.length === 0
  const isPrivateEventsAvailabilityEmpty = !showPrivateEvents || (!isExperiencesFetching && !experiencesToDisplay?.length)

  if (isPrimaryAvailabilityEmpty && isPrivateEventsAvailabilityEmpty) {
    // Call onNoAvailabilities() asynchronously so that the rendering of this component completes
    // first before calling the method. This callback function can now change the state of the
    // parent without any problems.
    setTimeout(() => {
      onNoAvailabilities(venue.venueId)
    })

    if (!isGroupWidget) {
      return null
    }
  }

  return (
    <VStack spacing="lm">
      <OtherAvailability
        title={venue.venueName}
        subtitle={venueShortDescription}
        scrollLeftLabel={formatMessage(otherVenuesAvailabilityMessages.scrollLeftLabel, { venue: venue.venueName })}
        scrollRightLabel={formatMessage(otherVenuesAvailabilityMessages.scrollRightLabel, { venue: venue.venueName })}
        scrollRightMargin={scrollRightMargin}
        currencyCode={currencyCode}
        onTitleClick={onVenueClick}
        onTimeSlotClick={onTimeSlotClickCallback}
        timeslots={data?.reservationTimesInHalo || []}
        titleDataTest={`other-venue-availability-${venue.urlKey}`}
        subtitleDataTest={`other-venue-availability-subtitle-${venue.urlKey}`}
        isFetching={isPrimaryAvailabilityFetching}
        noAvailabilityMessage={isPrimaryAvailabilityEmpty && isPrivateEventsAvailabilityEmpty ? noAvailabilityMessage : undefined}
        shouldUsePrimaryColor={isGroupWidget}
      />
      <OtherPrivateEventsAvailability
        startDate={startDate}
        partySize={partySize}
        experiences={experiencesToDisplay}
        isLoading={isExperiencesFetching}
        direction={timeSlotsDirection}
        timeSlotVariant={isGroupWidget ? 'primary' : 'secondary'}
        onTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability) =>
          onPrivateEventsTimeSlotClick?.(slot, experience, venue)
        }
        onMoreDetailsClick={onPrivateEventsMoreDetailsClick}
        onShowMoreClick={onShowMoreClick}
        scrollRightMargin={scrollRightMargin}
        useHorizontalScroll={!isMobile && (experiencesToDisplay?.length || 0) > 2}
        hideRequestButton
      />
    </VStack>
  )
}
